// addYakuLogic — the PURE, testable core of the AddYaku panel (slug, draft->YakuDefMeta, presets,
// and StructuralConstraint composer helpers). The React component is a thin shell over these so the
// authoring invariants run in the node test environment. No I/O, no React, no DOM.
import type { StructuralConstraint, TileId, YakuDefMeta } from '@mj/protocol';
import { CUSTOM_YAKU_SOURCE } from '../store';

// ---------------------------------------------------------------------------
// slug — derive a stable, url-safe id from a display name. Lowercase, [a-z0-9-] only, no edge/repeat
// hyphens. Non-latin names (e.g. kanji) reduce to their latin/number runs (the en name is the seed).
// ---------------------------------------------------------------------------
export function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-') // any run of non-[a-z0-9] becomes a single hyphen
    .replace(/^-+|-+$/g, ''); // trim leading/trailing hyphens
}

// ---------------------------------------------------------------------------
// Draft — the editable form state the panel collects.
// ---------------------------------------------------------------------------
export interface YakuDraft {
  id: string;
  nameJa: string;
  nameEn: string;
  kind: 'normal' | 'yakuman';
  defaultHan: number;
  yakumanMult: number;
  descJa: string;
  descEn: string;
  exampleHand: TileId[];
  constraint: StructuralConstraint;
}

export function emptyDraft(): YakuDraft {
  return {
    id: '',
    nameJa: '',
    nameEn: '',
    kind: 'yakuman',
    defaultHan: 13,
    yakumanMult: 1,
    descJa: '',
    descEn: '',
    exampleHand: [],
    // a harmless default node the user immediately edits.
    constraint: { form: 'chiitoi' },
  };
}

// ---------------------------------------------------------------------------
// buildYakuDef — assemble a schema-valid, custom-tagged YakuDefMeta from a draft. Custom yaku are
// always category 'localFamous' (per the milestone) and tunable 'structural' for yakuman (their
// multiplier, not han, is the knob); normal custom yaku are 'tunable' so the room can adjust han.
// ---------------------------------------------------------------------------
export function buildYakuDef(d: YakuDraft): YakuDefMeta {
  const detector: YakuDefMeta['detector'] = { type: 'dsl', constraint: d.constraint };
  const base: YakuDefMeta = {
    id: d.id,
    name: { ja: d.nameJa, en: d.nameEn },
    kind: d.kind,
    defaultHan: d.kind === 'yakuman' ? 13 : d.defaultHan,
    tunable: d.kind === 'yakuman' ? 'structural' : 'tunable',
    category: 'localFamous',
    detector,
    exampleHand: d.exampleHand,
    description: { ja: d.descJa, en: d.descEn },
    source: CUSTOM_YAKU_SOURCE,
  };
  if (d.kind === 'yakuman') base.yakumanMult = d.yakumanMult;
  return base;
}

// ---------------------------------------------------------------------------
// Presets — ready-made famous-local yaku to seed the form. Each carries a constraint, an example
// hand, and the metadata draft fields. Picking one fills the form; the user can then tweak + add.
// ---------------------------------------------------------------------------
export interface YakuPreset {
  id: string;
  label: string;
  constraint: StructuralConstraint;
  draft: Partial<YakuDraft>;
}

const PIN_2_8: TileId[] = ['p2', 'p2', 'p3', 'p3', 'p4', 'p4', 'p5', 'p5', 'p6', 'p6', 'p7', 'p7', 'p8', 'p8'];
const TANYAO: TileId[] = ['m2', 'm3', 'm4', 'p2', 'p3', 'p4', 's5', 's6', 's7', 's3', 's4', 's5', 'p8', 'p8'];

export const PRESETS: YakuPreset[] = [
  {
    id: 'daisharin',
    label: '大車輪 (Daisharin — pin 2..8 chiitoi)',
    constraint: { all: [{ form: 'chiitoi' }, { allTilesIn: { suit: 'p', rankRange: [2, 8] } }] },
    draft: {
      nameJa: '大車輪', nameEn: 'Daisharin', kind: 'yakuman', yakumanMult: 1,
      descJa: '筒子2〜8の七対子', descEn: 'Seven pairs of pinzu 2..8', exampleHand: PIN_2_8,
    },
  },
  {
    id: 'renhou',
    label: '人和 (Renhou — first-turn non-dealer ron)',
    constraint: { ctx: { firstTurn: true, ronOnly: true, nonDealer: true } },
    draft: {
      nameJa: '人和', nameEn: 'Renhou', kind: 'normal', defaultHan: 5,
      descJa: '子の第一巡ロン和了', descEn: 'First-turn non-dealer ron', exampleHand: TANYAO,
    },
  },
];

// ---------------------------------------------------------------------------
// Constraint composer helpers — build/edit the StructuralConstraint tree node-by-node. The panel
// drives these from buttons; kept pure so a future test can pin tree edits.
//
// Every member of the StructuralConstraint union has a ConstraintKind here so the visual editor can
// author ANY predicate (the project promise: rules are EDITABLE DATA, not JSON-only). The order
// below is the order shown in the selector.
// ---------------------------------------------------------------------------
export type ConstraintKind =
  | 'form'
  | 'allTilesIn'
  | 'groups'
  | 'identicalSequences'
  | 'consecutiveTriplets'
  | 'sameSequenceSuits'
  | 'consecutiveSequences'
  | 'everyGroupTouches'
  | 'pair'
  | 'winTile'
  | 'wait'
  | 'tilesSubsetOf'
  | 'suitsUsed'
  | 'ctx'
  | 'all'
  | 'any'
  | 'none';

/** All kinds in selector order — used by the UI to render the <option> list. */
export const CONSTRAINT_KINDS: readonly ConstraintKind[] = [
  'form',
  'allTilesIn',
  'groups',
  'identicalSequences',
  'consecutiveTriplets',
  'sameSequenceSuits',
  'consecutiveSequences',
  'everyGroupTouches',
  'pair',
  'winTile',
  'wait',
  'tilesSubsetOf',
  'suitsUsed',
  'ctx',
  'all',
  'any',
  'none',
];

export function constraintKindOf(c: StructuralConstraint): ConstraintKind {
  if ('form' in c) return 'form';
  if ('allTilesIn' in c) return 'allTilesIn';
  if ('groups' in c) return 'groups';
  if ('identicalSequences' in c) return 'identicalSequences';
  if ('consecutiveTriplets' in c) return 'consecutiveTriplets';
  if ('sameSequenceSuits' in c) return 'sameSequenceSuits';
  if ('consecutiveSequences' in c) return 'consecutiveSequences';
  if ('everyGroupTouches' in c) return 'everyGroupTouches';
  if ('pair' in c) return 'pair';
  if ('winTile' in c) return 'winTile';
  if ('wait' in c) return 'wait';
  if ('tilesSubsetOf' in c) return 'tilesSubsetOf';
  if ('suitsUsed' in c) return 'suitsUsed';
  if ('ctx' in c) return 'ctx';
  if ('all' in c) return 'all';
  if ('any' in c) return 'any';
  return 'none';
}

/** A fresh leaf/combinator node of the requested kind (sensible empty/minimal defaults). The
 * defaults are deliberately permissive so a freshly-added node validates and (where the predicate
 * has a required field) carries a benign starting value the user then tunes. */
export function newConstraintNode(kind: ConstraintKind): StructuralConstraint {
  switch (kind) {
    case 'form':
      return { form: 'chiitoi' };
    case 'allTilesIn':
      return { allTilesIn: {} };
    case 'groups':
      return { groups: {} };
    case 'identicalSequences':
      return { identicalSequences: {} };
    case 'consecutiveTriplets':
      return { consecutiveTriplets: { atLeast: 3 } };
    case 'sameSequenceSuits':
      return { sameSequenceSuits: { atLeast: 3 } };
    case 'consecutiveSequences':
      return { consecutiveSequences: { atLeast: 3 } };
    case 'everyGroupTouches':
      return { everyGroupTouches: {} };
    case 'pair':
      return { pair: {} };
    case 'winTile':
      return { winTile: {} };
    case 'wait':
      return { wait: { in: [] } };
    case 'tilesSubsetOf':
      return { tilesSubsetOf: { tiles: [] } };
    case 'suitsUsed':
      return { suitsUsed: {} };
    case 'ctx':
      return { ctx: {} };
    case 'all':
      return { all: [] };
    case 'any':
      return { any: [] };
    case 'none':
      return { none: [] };
  }
}
